import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';

interface SoundEffectContextType {
  playClick: () => void;
  playSound: (soundPath: string) => void;
}

const SoundEffectContext = createContext<SoundEffectContextType | undefined>(
  undefined
);

interface SoundEffectProviderProps {
  children: React.ReactNode;
  clickSoundUrl?: string;
  volume?: number;
  enableGlobalClickSound?: boolean;
}

export const SoundEffectProvider = ({
  children,
  clickSoundUrl = '/music/button-click.mp3',
  volume = 0.5,
  enableGlobalClickSound = true,
}: SoundEffectProviderProps) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const clickBufferRef = useRef<AudioBuffer | null>(null);
  const soundBufferCacheRef = useRef<Map<string, AudioBuffer>>(new Map());
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Initialize Web Audio API (much lower latency than HTML5 Audio)
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContextClass();
    audioContextRef.current = audioContext;

    // Preload and decode click sound buffer
    const loadClickSound = async () => {
      try {
        const response = await fetch(clickSoundUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        clickBufferRef.current = audioBuffer;
        setIsReady(true);
      } catch (error) {
        console.error('Failed to load click sound:', error);
      }
    };

    loadClickSound();

    return () => {
      audioContext.close();
    };
  }, [clickSoundUrl]);

  const playClick = useCallback(() => {
    const audioContext = audioContextRef.current;
    const clickBuffer = clickBufferRef.current;

    if (!audioContext || !clickBuffer) return;

    // Resume AudioContext if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    // Create a new buffer source for each play (allows overlapping)
    const source = audioContext.createBufferSource();
    const gainNode = audioContext.createGain();

    source.buffer = clickBuffer;
    gainNode.gain.value = volume;

    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Play immediately with zero latency
    source.start(0);
  }, [volume]);

  const playSound = useCallback(
    async (soundPath: string) => {
      const audioContext = audioContextRef.current;
      if (!audioContext) return;

      // Check cache first
      let buffer = soundBufferCacheRef.current.get(soundPath);

      if (!buffer) {
        try {
          const response = await fetch(soundPath);
          const arrayBuffer = await response.arrayBuffer();
          buffer = await audioContext.decodeAudioData(arrayBuffer);
          soundBufferCacheRef.current.set(soundPath, buffer);
        } catch (error) {
          console.error('Failed to load sound:', soundPath, error);
          return;
        }
      }

      // Resume AudioContext if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      // Create and play
      const source = audioContext.createBufferSource();
      const gainNode = audioContext.createGain();

      source.buffer = buffer;
      gainNode.gain.value = volume;

      source.connect(gainNode);
      gainNode.connect(audioContext.destination);

      source.start(0);
    },
    [volume]
  );

  // Global click sound effect
  useEffect(() => {
    if (!enableGlobalClickSound || !isReady) return;

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if clicked element is interactive
      const isInteractive =
        target.tagName === 'BUTTON' ||
        target.closest('button') ||
        target.classList.contains('cursor-pointer') ||
        target.closest('.cursor-pointer') ||
        target.getAttribute('role') === 'button' ||
        target.closest('[role="button"]') ||
        target.onclick !== null ||
        target.closest('[onclick]');

      if (isInteractive) {
        playClick();
      }
    };

    document.addEventListener('click', handleGlobalClick, { passive: true });

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [enableGlobalClickSound, isReady, playClick]);

  return (
    <SoundEffectContext.Provider value={{ playClick, playSound }}>
      {children}
    </SoundEffectContext.Provider>
  );
};

export const useSoundEffect = () => {
  const context = useContext(SoundEffectContext);
  if (context === undefined) {
    throw new Error('useSoundEffect must be used within a SoundEffectProvider');
  }
  return context;
};
